import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Container,
  Button,
  UncontrolledDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import Router, { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import qs from 'qs';

import _ from 'lodash';
import SearchNoResult from '../SearchNoResult';
import domainModel from 'domain-model';
import ArticleList from './ArticleList';
import getAxiosInstance from '../../config/api';
import CategoryTabs from '../CategoryTab';
import BannerTemplate from '../templates/template-components/BannerComponent';
import ArticleCarousel, { loadCarouselArticles } from './ArticleCarousel';
import FilterPanel from '../filters/FilterPanel';
import FilterPanelModal from '../filters/FilterPanelModal';
import loginAwareComponent from '../LoginAwareComponent';

const getCarouselConfigsForCurrentCategory = (
  pageConfig,
  querySpecification,
) => {
  const categories = _.get(pageConfig, 'layout.categories') || [];
  const currentCategory =
    categories.find(x =>
      _.isEqual(qs.stringify(x.query), qs.stringify(querySpecification)),
    ) || {};
  return _.get(currentCategory, 'carousels');
};

const getCarousels = (carouselConfigs, req) =>
  Promise.all(
    carouselConfigs.map(async carouselConfig => ({
      articles: await loadCarouselArticles(carouselConfig, req),
      config: carouselConfig,
    })),
  );

const getArticles = async (searchName, querySpecification, page, sort, req) => {
  const isSearchString = !!searchName;

  const query = qs.stringify({
    query: querySpecification,
    page,
    sort: sort || (isSearchString ? 'similarity.-name' : ''),
    searchString: searchName,
  });
  const axiosInstance = await getAxiosInstance(req);
  const response = await axiosInstance.get(`/public/articles?${query}`);
  return response.data;
};

const SortButton = ({ sort, router }) => {
  const getSortButtonText = () => {
    switch (sort) {
      case '-popularity':
        return 'Sortera på: Popularitet';
      case '-salesPrice':
        return 'Sortera på: Pris högt till lågt';
      case 'salesPrice':
        return 'Sortera på: Pris lågt till högt';
      case '-currentNewPeriodStarted,-isOrderable':
        return 'Sortera på: Nyheter';
      case '-articleNumber':
        return 'Artikelnummer';
      default:
        return 'Sortera';
    }
  };

  const setSort = sort => {
    const query = qs.parse(router.asPath.split('?')[1]);
    Router.push(
      {
        pathname: router.asPath.split('?')[0],
        query: { ...query, sort },
      },
      {
        pathname: router.asPath.split('?')[0],
        query: { ...query, sort },
      },

      { shallow: false },
    );
  };
  return (
    <UncontrolledDropdown>
      <DropdownToggle className="styled-btn" outline color="dark">
        <div className="styled-btn-text">{getSortButtonText()}</div>
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem
          onClick={() => setSort('-currentNewPeriodStarted,-isOrderable')}
        >
          Nyheter
        </DropdownItem>
        <DropdownItem onClick={() => setSort('-popularity')}>
          Popularitet
        </DropdownItem>
        <DropdownItem onClick={() => setSort('salesPrice')}>
          Pris: lågt till högt
        </DropdownItem>
        <DropdownItem onClick={() => setSort('-salesPrice')}>
          Pris: högt till lågt
        </DropdownItem>
        <DropdownItem onClick={() => controller.setSort('-articleNumber')}>
          Artikelnummer
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

function Articles({
  initialArticleListData,
  querySpecification,
  initialCarousels,
  pageConfig,
  searchName,
  filterFilter,
  shopConfig,
  hideShortcuts,
  sort,
}) {
  const [state, setState] = useState({
    articles: initialArticleListData.rows,
    carousels: initialCarousels,
  });

  const router = useRouter();

  const updateUrl = filterParams => {
    const query = {
      ...router.query,
      querySpecification: qs.stringify(filterFilter(filterParams)),
    };

    Router.push(
      {
        pathname: router.pathname,
        query,
      },
      {
        pathname: router.asPath.split('?')[0],
        query: {
          querySpecification: query.querySpecification,
        },
      },
      // TODO when Next 10: check if this can be refactored.
      { shallow: query.querySpecification === router.query.querySpecification },
    );
  };

  useEffect(() => {
    setState({
      articles: initialArticleListData.rows,
      carousels: initialCarousels,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialArticleListData, sort]);

  const { articles, carousels } = state;
  const categories = _.get(pageConfig, 'layout.categories');
  const banner = _.get(pageConfig, 'layout.banner');

  const [showFilterPanel, setShowFilterPanel] = useState(true);

  const hasFilterPanel = _.get(pageConfig, 'layout.hasFilterPanel', null);
  const weAreOnTheSearchPage = _.includes(router.asPath, 'articles');

  useEffect(() => {
    setShowFilterPanel(hasFilterPanel || weAreOnTheSearchPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySpecification]);

  const toggleShowFilters = hasFilter => {
    setShowFilterPanel((hasFilterPanel || weAreOnTheSearchPage) && hasFilter);
  };

  return (
    <>
      {banner && <BannerTemplate banner={banner} />}
      <Container id="content">
        <CategoryTabs
          onChange={updateUrl}
          querySpecification={querySpecification}
          categories={categories}
        />
        {carousels && (
          <Row className="justify-content-center mb-5">
            <Col xl={10} lg={9} md={12}>
              {carousels &&
                carousels.map(carousel => (
                  <Row key={carousel.config.id}>
                    <Col>
                      <ArticleCarousel carousel={carousel} />
                    </Col>
                  </Row>
                ))}
              {carousels.length > 0 && <hr />}
            </Col>
          </Row>
        )}
        <Row className="justify-content-end pe-1">
          {showFilterPanel && (
            <Col xs="auto" className="d-inline d-lg-none mb-3 text-end">
              <FilterPanelModal
                onChange={updateUrl}
                querySpecification={querySpecification}
                shopConfig={shopConfig}
                articleListConfig={{
                  showFilterPanel: showFilterPanel,
                  hideShortcuts: hideShortcuts,
                }}
              />
            </Col>
          )}
          <Col xs="auto" lg="12" className="text-lg-end mb-3 mb-lg-2">
            <SortButton sort={sort} router={router} />
          </Col>
        </Row>
        <Row className="g-0">
          {showFilterPanel && (
            <Col xs={3} className="d-none d-lg-block p-1 pe-2">
              <FilterPanel
                style={{
                  position: 'sticky',
                  top: domainModel.shopConfig.getNavbarHeight(shopConfig),
                }}
                onChange={updateUrl}
                querySpecification={querySpecification}
                hasValueCallback={toggleShowFilters}
                shopConfig={shopConfig}
                articleListConfig={{
                  showFilterPanel: showFilterPanel,
                  hideShortcuts: false,
                }}
              />
            </Col>
          )}
          <Col xs={12} lg={showFilterPanel ? 9 : 12}>
            <Row>
              <Col>
                {searchName && (
                  <>
                    <h3 className="d-inline-block">
                      Sökning &quot;{searchName}&quot;
                    </h3>
                    <Button
                      size="sm"
                      color="none"
                      className="btn-link text-muted d-inline-block float-end"
                      onClick={() => Router.push(`/articles`)}
                    >
                      Visa alla artiklar &gt;&gt;
                    </Button>
                  </>
                )}
              </Col>
            </Row>
            {!articles || articles.length === 0 ? (
              <SearchNoResult />
            ) : (
              <ArticleList
                initialArticleListData={initialArticleListData}
                querySpecification={querySpecification}
                getArticles={(querySp, p, r) =>
                  getArticles(searchName, querySp, p, r)
                }
              />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
}

Articles.getInitialProps = async ({
  req,
  querySpecification,
  pageConfig,
  searchName,
  shopConfig,
  sort,
}) => {
  const carouselConfigs = getCarouselConfigsForCurrentCategory(
    pageConfig,
    querySpecification,
  );
  let initialCarousels;
  if (carouselConfigs) {
    initialCarousels = await getCarousels(carouselConfigs, req);
  }
  const firstPage = 1;
  const initialArticleListData = await getArticles(
    searchName,
    querySpecification,
    firstPage,
    sort,
    req,
  );

  return {
    initialArticleListData,
    initialCarousels,
    pageConfig,
    searchName,
    shopConfig,
    sort,
    querySpecification,
  };
};

Articles.propTypes = {
  initialArticleListData: PropTypes.object.isRequired,
  querySpecification: PropTypes.object,
  pageConfig: PropTypes.object.isRequired,
  shopConfig: PropTypes.object.isRequired,
  initialCarousels: PropTypes.array,
  searchName: PropTypes.string,
  filterFilter: PropTypes.func,
  hideShortcuts: PropTypes.bool,
};

Articles.defaultProps = {
  initialCarousels: null,
  searchName: undefined,
  querySpecification: {},
  filterFilter: x => x,
  hideShortcuts: true,
};

export default loginAwareComponent(Articles);
